/**
 * Revision History
 * 
 * 11/09/2021 ptkim     Add PasswordRenderer
 */
import numeral from 'numeral';
import React from 'react';

import CheckBox from '../ui/CheckBox';
import CheckBoxIcon from '../ui/CheckBoxIcon';
import {formatDate, formatDateTime} from '../dateUtils';

import {faSearch} from "@fortawesome/free-solid-svg-icons";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import {getDecimal} from '../util';
import PrimaryInverseButton from "../ui/PrimaryInverseButton";
import useSuntekSettings from '../hooks/useSuntekSettings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function NumberedIndicatorRendererImpl({row}:any) {
  return <div className="ifms-table-default">{row.index + 1}</div>;
}

function IndicatorRendererImpl() {
  return <div>&nbsp;</div>;
}

function TableIndicatorRendererImpl({rowIndex}:any) {
  return <div className="ifms-table-default ifms-table-indicator table-cell-disabled">{rowIndex + 1}</div>;
}

function TableCheckBoxRendererImpl({ rowIndex, columnId, value, disabled, onChangeFunc }: any) {
  return (
    <div className={disabled ? 'table-cell-disabled' : ''}>
      <CheckBox id={`${columnId}-${rowIndex}`} name={`${columnId}-${rowIndex}`} disabled={disabled} value={value} onChange={chk => onChangeFunc(chk)} />
    </div>
  );
}

function TableNumberRendererImpl({ rowIndex, columnId, value, disabled, align, decimal, showZero }: any) {
  const formatNumber = (value: number | null): string | null => {
    if (value === null) return null;

    const format = `0,0.${'0'.repeat(decimal)}`;
    const formattedValue = numeral(value).format(format);

    return (!showZero && Number(formattedValue) === 0 && disabled) ? null : formattedValue;
  };

  const formattedValue = formatNumber(value);

  let className = `ifms-table-align-${align} ifms-table-number`;
  if (disabled) {
    className += " table-cell-disabled"
  }

  return (
    <div id={`${columnId}-${rowIndex}`} className={className}>
      {formattedValue}
    </div>
  );
}

function TableTextRendererImpl({ rowIndex, columnId, value, disabled, align, onLabelClick, fontColor, showDesc }: any) {
  let className = `ifms-table-align-${align}`;
  if (disabled) {
    className += " table-cell-disabled"
  }

  const clickProps =  
    //LabelClick이 가능할때 fontColor도 변경할 수 있도록 조건 순서 변경
    onLabelClick && (fontColor === 'red') ? {
      onClick: onLabelClick,
      style: {
        cursor: 'pointer',
        color: fontColor, 
        fontWeight: 600,
      }
    }
    : onLabelClick ? {
      onClick: onLabelClick,
      style: {
        cursor: 'pointer',
        color: 'blue', 
        fontWeight: 600,
      }
    }
    : {};

  const textStyle = !onLabelClick && fontColor
    ? { color: fontColor }
    : {};

  return (
    <div
      {...clickProps}
      id={`${columnId}-${rowIndex}`}
      className={className}
      style={{...clickProps.style, ...textStyle}}
    >
      <span style={{ color: showDesc && !value ? '#999999' : 'inherit' }}>
        {showDesc && !value ? 'Enter Max 100 chars.' : value}
      </span>
    </div>
  );
}

function TableLookupRendererImpl({ rowIndex, columnId, value, disabled, headerWidth }: any) {
  let className = 'ifms-table-align-left';
  if (disabled) {
    className += " table-cell-disabled";
  }

  return (
    <div
      id={`${columnId}-${rowIndex}`}
      className={className}
    >
      <>
        <span
          data-tooltip-cutoff-target
          style={{
            maxWidth: `calc(${headerWidth - 25}px)`,
            overflow: 'hidden',
            display: 'block',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
        <FontAwesomeIcon
          icon={faSearch}
          style={{
            position: 'absolute',
            right: '5px',
            fontSize: '10px',
            top: '6px',
            color: '#a1a1a1'
          }}
        />
      </>
    </div>
  );
}

function TableComboRendererImpl({ rowIndex, columnId, value, disabled, headerWidth }: any) {
  let className = 'ifms-table-align-left';
  if (disabled) {
    className += " table-cell-disabled";
  }

  return (
    <div
      id={`${columnId}-${rowIndex}`}
      className={className}
    >
      <>
        <span
          style={{
            maxWidth: `calc(${headerWidth - 18}px)`,
            overflow: 'hidden',
            display: 'block'
          }}
        >
          {value}
        </span>
        <FontAwesomeIcon
          icon={faCaretDown}
          style={{
            position: 'absolute',
            right: '3px',
            fontSize: '11px',
            top: '5px',
            color: '#a1a1a1'
          }}
        />
      </>
    </div>
  );
}

function TableDateRendererImpl({ rowIndex, columnId, value, disabled, dateType }: any) {
  const suntekSettings = useSuntekSettings();
  const dateFormat = suntekSettings.company?.dateFormat.toUpperCase() || '';

  let className = 'ifms-table-align-center';
  if (disabled) {
    className += " table-cell-disabled"
  }

  let renderValue = null;
  if (dateType === 'dateonly') {
    renderValue = formatDate(value, dateFormat);
    className += " ifms-table-date"
  }
  else if (dateType === 'datetime') {
    renderValue = formatDateTime(value, dateFormat, false, true);
    className += " ifms-table-datetime"
  }
  else if (dateType === 'datetimesec') {
    renderValue = formatDateTime(value, dateFormat, true, true);
    className += " ifms-table-datetimesec"
  }

  // const isPlaceholder = _.isEmpty(renderValue) && !disabled;
  // const displayValue = isPlaceholder ? dateFormat : renderValue;

  // const style = isPlaceholder
  //   ? { color: '#999999'}
  //   : undefined;

  return (
    <div
      id={`${columnId}-${rowIndex}`}
      className={className}
      // style={style}
    >
      {renderValue}
    </div>
  );
}

function TableDisableCheckboxIconRendererImpl({row, cell}:any) {
  if (cell && cell.value) {
    return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default table-cell-disabled">
      <CheckBoxIcon value={true} />
    </div>;  
  }

  return <div className="ifms-table-default table-cell-disabled ifms-ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function TextRendererImpl({row, cell}:any) {
  return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default">{cell.value}</div>;
}

function RedTextRendererImpl({row, cell}:any) {
  return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default" style={{color: 'red'}}>{cell.value}</div>;
}

function TextAlignLeftRendererImpl({row, cell, accountName}:any) {
  const displayValue = accountName ? accountName : cell.value;
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-left">
      {displayValue}
    </div>
  );
}

function RedTextAlignLeftRendererImpl({row, cell, accountName}:any) {
  const displayValue = accountName ? accountName : cell.value;
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-left" style={{color: 'red'}}>
      {displayValue}
    </div>
  );
}

function TextAlignLeftWithBordersRendererImpl({cell}:any) {
  return (
    <div className="ifms-table-cell-left-border" style={{width: '100%'}} id={`${cell.column.id}-${cell.row.index}`}>
      {cell.value}
    </div>
  );
}

function TextAlignLeftWithAddressTooltipRendererImpl({ cell }: any) {
  let tempAddressText = '';
  let cellText = cell.value;
  switch (cell.column.Header) {
    case 'Shipper':
      //Air house = original.shipper / Ocean house = original.sName
      //tempAddressText = cell.row.original.sName ? cell.row.original.sName : cell.row.original.shipper; 
      tempAddressText = cell.value;
      cellText = cellText?.split('\n')[0].replace('\r','');
      break;
    case 'Consignee':
      //Air house = original.consignee / Ocean house = original.cName
      //tempAddressText = cell.row.original.cName ? cell.row.original.cName : cell.row.original.consignee;
      tempAddressText = cell.value;
      cellText = cellText?.split('\n')[0].replace('\r','');
      break;
    case 'Notify':
      //Air house = original.notify / Ocean house = original.nName
      //tempAddressText = cell.row.original.nName ? cell.row.original.nName : cell.row.original.notify;
      tempAddressText = cell.value;
      cellText = cellText?.split('\n')[0].replace('\r','');
      break;
    case 'Broker':
      tempAddressText = cell.row.original.bName;
      cellText = cellText?.split('\n')[0].replace('\r','');
      break;
    case 'Customs Broker': //OEH
      tempAddressText = cell.row.original.bName;
      break;
    case 'P/U Cargo Location': //OE Booking
      tempAddressText = cell.row.original.puCargoLocation;
      break;
    case 'Trucking Vendor': //OE Booking
      tempAddressText = cell.row.original.truckingCompany;
      break;
  }
  return <div className="ifms-table-align-left" data-tooltip={tempAddressText} id={`${cell.column.id}-${cell.row.index}`}>{cellText} </div>;
}

function TextAlignRightRendererImpl({cell, row}:any) {
  return (
      <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-right">{cell.value}</div>
  );
}

function CheckboxIconRendererImpl({cell}:any) {
  return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
    <CheckBoxIcon value={cell?.value} />
  </div>;
}

function NullableCheckboxIconRendererImpl({row, cell}:any) {
  if (cell && cell.value) {
    return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default">
      <CheckBoxIcon value={true} />
    </div>;  
  }

  return <div className="ifms-table-default ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function NullableCheckboxRedIconRendererImpl({cell}:any) {
  if (cell && cell.value) {
    return <div id={`${cell.column.id}-${cell.row.index}`} className="ifms-table-default">
      <CheckBoxIcon value={true} color="red"/>
    </div>;  
  }

  return <div className="ifms-table-default ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function NullableDangerCheckboxIconRendererImpl({cell}:any) {
  if (cell && cell.value) {
    return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
      <CheckBoxIcon value={true} color="red" />
    </div>;  
  }

  return <div className="ifms-table-default ifms-check-box-icon-false" id={`${cell.column.id}-${cell.row.index}`}></div>;
}

function NullableBlackCheckboxIconRendererImpl({cell}:any) {
  if (cell && cell.value) {
    return <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
      <CheckBoxIcon value={true} color="black" />
    </div>;  
  }

  return null;
}

function DateRendererImpl({cell, row}:any) {
const suntekSettings = useSuntekSettings();
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default ifms-table-date">
      {formatDate(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '')}
    </div>
  );
}

function RedDateRendererImpl({cell, row}:any) {
const suntekSettings = useSuntekSettings();
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default ifms-table-date" style={{color: 'red'}}>
      {formatDate(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '')}
    </div>
  );
}

function DateTimeRendererImpl({cell, format}:any) {
  const suntekSettings = useSuntekSettings();
  return (
    <div className="ifms-table-default ifms-table-datetime" id={`${cell.column.id}-${cell.row.index}`}> 
      {formatDateTime(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '', false, true)}
    </div>
  );
}

function RedDateTimeRendererImpl({cell, format}:any) {
  const suntekSettings = useSuntekSettings();
  return (
    <div className="ifms-table-default ifms-table-datetime" style={{color: 'red'}} id={`${cell.column.id}-${cell.row.index}`}> 
      {formatDateTime(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '', false, true)}
    </div>
  );
}

function DateTimeIncludeSecRendererImpl({cell, format}:any) {
  const suntekSettings = useSuntekSettings();
  return (
    <div className="ifms-table-default ifms-table-datetimesec" id={`${cell.column.id}-${cell.row.index}`}>
      {formatDateTime(cell.value, suntekSettings.company?.dateFormat.toUpperCase() || '', true, true)}
    </div>
  );
}

// function CurrencyRendererImpl({data, row, cell}:any) {
//   const value = cell.value ? cell.value.currencyCode : '';
//   return (
//     <div className="ifms-table-default" id={`${cell.column.id}-${cell.row.index}`}>
//       {value}
//     </div>
//   );
// }

function CurrencyAmountRendererImpl({currencyAccessor, data, row, cell}:any) {
  const currency = currencyAccessor(data[row.index]);
  let amount = '';
  const cellValue = cell.value;
  const decimals = getDecimal(currency);
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    const format = `0,0.${'0'.repeat(decimals)}`;
    amount = numeral(cellValue.toFixed(decimals)).format(format);
  }

  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-currency-amount">
      {amount}
      {/* <CurrencyLabel value={cell.value} currency={currency} showZero={true} /> */}
    </div>
  );
}

// 22.12.27 hykim - 정수의 자리 구분이 필요 없는 경우를 추가하기 위해 useComma를 추가함
// function FloatRendererImpl({decimals = 2, cell}:any) {
function FloatRendererImpl({useComma = true, decimals = 2, disabled, row, cell}:any) {
  let amount = '';
  let formattedNumber = '';
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    // const format = `'0,0'.${'0'.repeat(decimals)}`;
    const format = `${useComma ? '0,0' : '0'}.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: useComma }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  let className = "ifms-table-align-right  ifms-table-float"
  if(disabled) className += " table-cell-disabled"
  return (
    <div id={`${cell.column.id}-${row.index}`} className={className} >{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}

function RedFloatRendererImpl({useComma = true, decimals = 2, disabled, row, cell, style}:any) {
  let amount = '';
  let formattedNumber = '';
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    // const format = `'0,0'.${'0'.repeat(decimals)}`;
    const format = `${useComma ? '0,0' : '0'}.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: useComma }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  let className = "ifms-table-align-right  ifms-table-float"
  if(disabled) className += " table-cell-disabled"
  return (
    <div id={`${cell.column.id}-${row.index}`} className={className} style={{color: 'red'}} >{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}


function FloatCurRendererImpl({row, cell, useComma = true, currency = 'USD'}:any) {
  let amount = '';
  let formattedNumber = '';
  const decimals = getDecimal(currency);
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    const format = `${useComma ? '0,0' : '0'}.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: useComma }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  return (
    <div id={`${cell.column.id}-${row.index}`} className="ifms-table-align-right" >{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}

function FloatWithBorderRendererImpl({decimals = 2, cell}:any) {
  let amount = '';
  let formattedNumber = '';
  const cellValue = cell.value;
  if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
    const format = `0,0.${'0'.repeat(decimals)}`;
    formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(cellValue);
    amount = numeral(cellValue).format(format);
  }
  return (
    <div className="ifms-table-cell-right-border">{amount === 'NaN' ? formattedNumber : amount}</div>
  );
}

function BlueTextRendererImpl({cell, tooltip = 'Click for Detail', bold = true, onClick}:any) {
  // return <span data-tooltip={tooltip} className="input-control-row-label-link">{cell.value}</span>
  return <PrimaryInverseButton style={{color: 'blue', fontWeight: bold ? 'bold' : 'normal', fontSize: '10px', padding: '0 !important'}} onClick={onClick} data-tooltip={tooltip}>{cell.value}</PrimaryInverseButton>
}

// //<pt211109>
// function PasswordRendererImpl({cell}: any) {
//   return <div style={{marginTop: '0px'}}>{'*'.repeat(cell.value.length)}</div>;
// }

export const NumberedIndicatorRenderer = React.memo(NumberedIndicatorRendererImpl);
export const IndicatorRenderer = React.memo(IndicatorRendererImpl);
export const TextRenderer = React.memo(TextRendererImpl);
export const RedTextRenderer = React.memo(RedTextRendererImpl);
export const TableTextRenderer = React.memo(TableTextRendererImpl);
export const TableLookupRenderer = React.memo(TableLookupRendererImpl);
export const TableComboRenderer = React.memo(TableComboRendererImpl);
export const TableDateRenderer = React.memo(TableDateRendererImpl);
export const TableNumberRenderer = React.memo(TableNumberRendererImpl)
export const TableCheckBoxRenderer = React.memo(TableCheckBoxRendererImpl)
export const TableIndicatorRenderer = React.memo(TableIndicatorRendererImpl)
export const TableDisableCheckBoxRenderer = React.memo(TableDisableCheckboxIconRendererImpl)
export const CheckboxIconRenderer = React.memo(CheckboxIconRendererImpl);
export const NullableCheckboxIconRenderer = React.memo(NullableCheckboxIconRendererImpl);
export const NullableDangerCheckboxIconRenderer = React.memo(NullableDangerCheckboxIconRendererImpl);
export const NullableBlackCheckboxIconRenderer = React.memo(NullableBlackCheckboxIconRendererImpl);
export const DateRenderer = React.memo(DateRendererImpl);
export const RedDateRenderer = React.memo(RedDateRendererImpl);
export const DateTimeRenderer = React.memo(DateTimeRendererImpl);
export const RedDateTimeRenderer = React.memo(RedDateTimeRendererImpl);
export const DateTimeIncludeSecRenderer = React.memo(DateTimeIncludeSecRendererImpl);
export const TextAlignLeftRenderer = React.memo(TextAlignLeftRendererImpl);
export const RedTextAlignLeftRenderer = React.memo(RedTextAlignLeftRendererImpl);
export const TextAlignLeftWithBordersRenderer = React.memo(TextAlignLeftWithBordersRendererImpl);
export const TextAlignRightRenderer = React.memo(TextAlignRightRendererImpl);
export const CurrencyAmountRenderer = React.memo(CurrencyAmountRendererImpl);
export const TextAlignLeftWithAddressTooltipRenderer = React.memo(TextAlignLeftWithAddressTooltipRendererImpl);
export const FloatRenderer = React.memo(FloatRendererImpl);
export const RedFloatRenderer = React.memo(RedFloatRendererImpl);
export const FloatCurRenderer = React.memo(FloatCurRendererImpl);
export const FloatWithBorderRenderer = React.memo(FloatWithBorderRendererImpl);
//export const PasswordRenderer = React.memo(PasswordRendererImpl);   //<pt211109>
export const BlueTextRenderer = React.memo(BlueTextRendererImpl);   //<pt211109>
export const NullableCheckboxRedIconRenderer = React.memo(NullableCheckboxRedIconRendererImpl);
